import { useCallback } from 'react';

import { useNoteSaves } from '../../contexts/note-saves';
import { useSession } from '../../contexts/session';
import { useRequest } from '../request';

// The sentence for a sign-out the server confirmed whose page could not be
// updated afterwards: the session is over, so it must not read as a sign-out
// that failed, and trying again simply finishes the job.
const signedOutNotShown = 'You are signed out. This page could not be updated; try again';

// The sentence for a sign-out held back because a clinician's note could not
// be saved (#132): the session is kept, signing out again tries the note
// again, and discarding signs out without it.
export const notesUnsaved = 'A note is not saved yet. Sign out again to try saving it, or sign out without it';

// Ends this device's session, then asks again who is signed in. The answer is
// nobody, and the router takes it from there back to the sign-in screen.
// Nothing is cleared before the server confirms: a request that never
// reaches it rejects with its own sentence and leaves the session as it was.
//
// Notes still saving are saved first, under the session they were written
// in; when one cannot be, the sign-out stops there with notesUnsaved, unless
// discard says to sign out without it. The whole of it, from the press to the
// session's end or the reason it stopped, is the note saves' shared sign-out
// transition (#134): no note takes an edit meanwhile, whichever screen is
// open, and the reason it stopped stays shown wherever the person went.
// While the session is being ended no new note write starts, and one that
// could not be ended lets them go again. Once it has ended, the note saves
// retire with it: nothing written before is sent under whoever signs in
// next. A session the service already ended has nothing left to save.
export function useSignOut() {
  const request = useRequest();
  const { refresh } = useSession();
  const { ended, settleAll, hold, release, startSignOut, stopSignOut } = useNoteSaves();

  return useCallback(function({ discard = false } = {}) {
    startSignOut();

    let saving = Promise.resolve(true);

    if (discard === false && ended === false) {
      saving = settleAll();
    }

    return saving.then(function(saved) {
      if (saved === false) {
        throw new Error(notesUnsaved);
      }

      hold();

      return request({ path: '/auth/session', method: 'DELETE' }).catch(function(failed) {
        if (ended === false) {
          release();
        }

        throw failed;
      });
    }).then(function() {
      return refresh().catch(function() {
        throw new Error(signedOutNotShown);
      });
    }).catch(function(failed) {
      stopSignOut({ failure: failed.message, ended: failed.message === signedOutNotShown });
      throw failed;
    });
  }, [ request, refresh, ended, settleAll, hold, release, startSignOut, stopSignOut ]);
}
